import React, { useState, useEffect, useCallback } from "react";
import apiService from "../services/api";
import PageTransition from "../components/PageTransition";
import ModernHeader from "../components/Modern/ModernHeader";
import ModernCard from "../components/Modern/ModernCard";
import {
  Calendar,
  Power,
  Settings,
  CheckCircle,
  XCircle,
  X,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Zap,
  Clock,
} from "lucide-react";
import { LoadingSpinner } from "../components/ui/loading-spinner";

// Estilos CSS personalizados para animações
const customStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
  
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  
  .float-animation {
    animation: float 3s ease-in-out infinite;
  }
  
  .shimmer-effect {
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    background-size: 200% 100%;
    animation: shimmer 2s infinite;
  }
  
  /* Estilos para seletores de horário customizados */
  select::-webkit-scrollbar {
    width: 8px;
  }
  
  select::-webkit-scrollbar-track {
    background: #374151;
  }
  
  select::-webkit-scrollbar-thumb {
    background: #6B7280;
    border-radius: 4px;
  }
  
  select::-webkit-scrollbar-thumb:hover {
    background: #9CA3AF;
  }
`;

// Adicionar estilos ao documento
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = customStyles;
  document.head.appendChild(styleSheet);
}

const Agendamento = () => {
  const [agendamentoAtivo, setAgendamentoAtivo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [isToggling, setIsToggling] = useState(false);
  const [agendamentoConfig, setAgendamentoConfig] = useState({
    domingo_ativo: false,
    segunda_ativo: false,
    terca_ativo: false,
    quarta_ativo: false,
    quinta_ativo: false,
    sexta_ativo: false,
    sabado_ativo: false,
    horarios_config: { inicio: '09:00', fim: '18:00' },
    horarios_especificos: {}
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [showConfigPanel, setShowConfigPanel] = useState(false);

  const diasSemana = [
    { key: 'domingo_ativo', label: 'Domingo', short: 'Dom' },
    { key: 'segunda_ativo', label: 'Segunda-feira', short: 'Seg' },
    { key: 'terca_ativo', label: 'Terça-feira', short: 'Ter' },
    { key: 'quarta_ativo', label: 'Quarta-feira', short: 'Qua' },
    { key: 'quinta_ativo', label: 'Quinta-feira', short: 'Qui' },
    { key: 'sexta_ativo', label: 'Sexta-feira', short: 'Sex' },
    { key: 'sabado_ativo', label: 'Sábado', short: 'Sáb' }
  ];

  // Carregar status do agendamento ao montar o componente
  const carregarStatusAgendamento = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.request('/ia/agendamento/status');
      setAgendamentoAtivo(response.agendamento);
    } catch (error) {
      showNotification('Erro ao carregar status do agendamento', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar configurações de agendamento
  const carregarConfiguracoes = useCallback(async () => {
    try {
      setConfigLoading(true);
      const response = await apiService.request('/ia/agendamento/config');
      setAgendamentoConfig(response.config);
    } catch (error) {
      showNotification('Erro ao carregar configurações de agendamento', 'error');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  // Salvar configurações de agendamento
  const salvarConfiguracoes = async () => {
    try {
      setConfigLoading(true);
      await apiService.request('/ia/agendamento/config', {
        method: 'PUT',
        body: JSON.stringify(agendamentoConfig)
      });
      showNotification('Configurações salvas com sucesso!', 'success');
    } catch (error) {
      showNotification('Erro ao salvar configurações', 'error');
    } finally {
      setConfigLoading(false);
    }
  };

  // Atualizar dia da semana
  const toggleDiaSemana = (diaKey) => {
    setAgendamentoConfig(prev => ({
      ...prev,
      [diaKey]: !prev[diaKey]
    }));
  };

  // Atualizar horários gerais (apenas início e fim)
  const updateHorarios = (field, value) => {
    setAgendamentoConfig(prev => ({
      ...prev,
      horarios_config: {
        ...prev.horarios_config,
        [field]: value
      }
    }));
  };

  useEffect(() => {
    carregarStatusAgendamento();
    carregarConfiguracoes();
  }, [carregarStatusAgendamento, carregarConfiguracoes]);

  // Função para mostrar notificações
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Função para fechar notificação manualmente
  const closeNotification = () => {
    setNotification(null);
  };

  // Função para alternar status do agendamento
  const toggleAgendamento = async () => {
    if (isToggling) return;
    
    try {
      setIsToggling(true);
      const novoStatus = !agendamentoAtivo;
      
      await apiService.request('/ia/agendamento/status', {
        method: 'PUT',
        body: JSON.stringify({ agendamento: novoStatus })
      });
      
      setAgendamentoAtivo(novoStatus);
      showNotification(
        `Agendamento ${novoStatus ? 'ativado' : 'desativado'} com sucesso!`,
        'success'
      );
    } catch (error) {
      showNotification('Erro ao alterar status do agendamento', 'error');
    } finally {
      setIsToggling(false);
    }
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="space-y-6">
          <ModernHeader 
            title="Agendamento IA"
            subtitle="Carregando configurações..."
            icon={Calendar}
            themeColor="green"
          />
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-8 border border-gray-200/50 dark:border-gray-700/50">
            <div className="flex flex-col items-center justify-center h-48">
              <LoadingSpinner size="md" />
            </div>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      {/* Componente de Notificação */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 max-w-sm w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border-l-4 ${
            notification.type === "success"
              ? "border-green-500"
              : "border-red-500"
          } transform transition-all duration-300 ease-in-out`}
        >
          <div className="p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {notification.type === "success" ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
              </div>
              <div className="ml-3 w-0 flex-1">
                <p
                  className={`text-sm font-medium ${
                    notification.type === "success"
                      ? "text-green-800"
                      : "text-red-800"
                  }`}
                >
                  {notification.type === "success" ? "Sucesso!" : "Erro!"}
                </p>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {notification.message}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0 flex">
                <button
                  onClick={closeNotification}
                  className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Header Modernizado */}
        <ModernHeader 
          title="Agendamento IA"
          subtitle="Configure e gerencie o sistema de agendamento automatizado"
          icon={Calendar}
          themeColor="green"
        >          
          <div className="hidden md:flex items-center space-x-2 mt-4">
            <Sparkles className="h-6 w-6 text-yellow-300 animate-pulse" />
            <span className="text-white/80 text-sm font-medium">Powered by AI</span>
          </div>
        </ModernHeader>

        {/* Status do Agendamento e Configurações */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
          {/* Status do Agendamento */}
          <ModernCard 
            className="p-4 sm:p-8 h-full"
            borderClass="border-2"
            glow
            themeColor="green"
          >
            <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl transition-all duration-300 bg-white border border-gray-300 shadow-none dark:bg-[#1a2230] dark:border-white/10">
                <Calendar className="h-6 w-6 text-[#222c3a] dark:text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#222c3a] dark:text-white">Status do Agendamento</h2>
            <p className="text-[#222c3a] dark:text-gray-400 text-sm">Controle principal do sistema</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200/50 dark:border-gray-700/50">
                <div className="relative">
                  <div className={`w-4 h-4 rounded-full transition-all duration-300 ${
                    agendamentoAtivo ? "bg-green-500 shadow-lg shadow-green-500/50" : "bg-red-500 shadow-lg shadow-red-500/50"
                  }`}>
                    {agendamentoAtivo && <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75"></div>}
                  </div>
                </div>
                <div>
            <span className="text-lg font-semibold text-[#222c3a] dark:text-white">
                    {agendamentoAtivo ? "Sistema Ativo" : "Sistema Inativo"}
                  </span>
                  <p className="text-sm text-[#222c3a] dark:text-gray-300">
                    {agendamentoAtivo ? "Agendamento funcionando" : "Agendamento pausado"}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={toggleAgendamento}
                  disabled={isToggling || agendamentoAtivo}
                  className={`flex-1 px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 transform relative overflow-hidden group ${
                    agendamentoAtivo
                      ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg shadow-green-500/25 hover:shadow-green-500/40 hover:scale-105"
                  }`}
                >
                  {!agendamentoAtivo && <div className="absolute inset-0 shimmer-effect opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>}
                  <Zap className="h-5 w-5 relative z-10" />
                  <span className="relative z-10">Ativar Agendamento</span>
                </button>
                <button
                  onClick={toggleAgendamento}
                  disabled={isToggling || !agendamentoAtivo}
                  className={`flex-1 px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 transform relative overflow-hidden group ${
                    !agendamentoAtivo
                      ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white shadow-lg shadow-red-500/25 hover:shadow-red-500/40 hover:scale-105"
                  }`}
                >
                  {agendamentoAtivo && <div className="absolute inset-0 shimmer-effect opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>}
                  <Power className="h-5 w-5 relative z-10" />
                  <span className="relative z-10">Pausar Agendamento</span>
                </button>
              </div>
            </div>
          </ModernCard>

          {/* Painel de Configurações */}
          <ModernCard 
            className="p-4 sm:p-8 h-full"
            borderClass="border-2"
            glow
            themeColor="green"
          >
            <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-white border border-gray-300 shadow-none dark:bg-[#1a2230] dark:border-white/10">
                <Settings className="h-6 w-6 text-[#222c3a] dark:text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#222c3a] dark:text-white">Configurações</h2>
            <p className="text-[#222c3a] dark:text-gray-400 text-sm">Personalize o agendamento</p>
              </div>
            </div>
            
            <div className="flex flex-col gap-6">
              <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200/50 dark:border-gray-700/50">
            <p className="text-[#222c3a] dark:text-gray-300 leading-relaxed">
                  Configure os dias da semana e horários de funcionamento do sistema de agendamento. Não esqueça de salvar as configurações após fazer as alterações.
                </p>
              </div>
              
              <div className="pt-2">
                <button
                  onClick={() => setShowConfigPanel(!showConfigPanel)}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all duration-300 transform hover:scale-105 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 shimmer-effect opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <Settings className="h-5 w-5 relative z-10" />
                  <span className="relative z-10">{showConfigPanel ? 'Ocultar' : 'Mostrar'} Configurações</span>
                  {showConfigPanel ? <ChevronUp className="h-5 w-5 relative z-10" /> : <ChevronDown className="h-5 w-5 relative z-10" />}
                </button>
              </div>
            </div>
          </ModernCard>
        </div>

        {/* Painel de Configurações Expandido */}
        {showConfigPanel && (
          <ModernCard
            className="p-4 sm:p-8"
          >
            <div className="relative">
              <div className="flex items-center gap-3 mb-8">
            <div className="p-3 rounded-xl bg-white border border-gray-300 shadow-none dark:bg-[#1a2230] dark:border-white/10">
                  <Clock className="h-6 w-6 text-[#222c3a] dark:text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-[#049769] dark:text-[#049769]">Configurações de Agendamento</h2>
            <p className="text-[#222c3a] dark:text-gray-400 text-sm">Defina dias e horários de funcionamento</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Coluna Esquerda - Dias da Semana */}
                <div className="space-y-6">
                  <div className="space-y-2">
            <label className="block text-sm font-semibold text-[#222c3a] dark:text-white mb-4">
                      Dias da Semana Ativos
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {diasSemana.map((dia) => (
                        <button
                          key={dia.key}
                          onClick={() => toggleDiaSemana(dia.key)}
                          className={`p-4 rounded-xl border-2 transition-all duration-300 transform hover:scale-105 ${
                            agendamentoConfig[dia.key]
                              ? 'bg-gradient-to-r from-blue-500 to-purple-600 border-blue-500 text-white shadow-lg shadow-blue-500/25'
                              : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-300'
                          }`}
                        >
                          <div className="text-center">
                            <div className="font-semibold text-sm">{dia.short}</div>
                            <div className="text-xs opacity-80 mt-1">{dia.label}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Coluna Direita - Horários */}
                <div className="space-y-6">
                  <div className="space-y-4">
            <label className="block text-sm font-semibold text-gray-700 dark:text-white mb-4">
                      Horários de Funcionamento
                    </label>
                    
                    <div className="grid grid-cols-2 gap-2">
                       <div className="space-y-1">
            <label className="block text-xs font-medium text-[#222c3a] dark:text-white">
                           Horário de Início
                         </label>
                         <div className="relative">
                           <div className="flex">
                             <select
                               value={agendamentoConfig.horarios_config.inicio.split(':')[0] || '09'}
                               onChange={(e) => {
                                 const minutes = agendamentoConfig.horarios_config.inicio.split(':')[1] || '00';
                                 updateHorarios('inicio', `${e.target.value}:${minutes}`);
                               }}
                             className="w-1/2 px-1 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-l text-[#111926] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#049769] focus:border-transparent transition-all duration-200 text-xs"
                             >
                               {Array.from({length: 24}, (_, i) => {
                                 const hour = i.toString().padStart(2, '0');
                                 return <option key={hour} value={hour}>{hour}</option>;
                               })}
                             </select>
                             <span className="flex items-center px-0.5 bg-white dark:bg-gray-700 border-t border-b border-gray-300 dark:border-gray-600 text-[#111926] dark:text-white text-xs">:</span>
                             <select
                               value={agendamentoConfig.horarios_config.inicio.split(':')[1] || '00'}
                               onChange={(e) => {
                                 const hours = agendamentoConfig.horarios_config.inicio.split(':')[0] || '09';
                                 updateHorarios('inicio', `${hours}:${e.target.value}`);
                               }}
                               className="w-1/2 px-1 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-r text-[#111926] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#049769] focus:border-transparent transition-all duration-200 text-xs"
                             >
                               {Array.from({length: 60}, (_, i) => {
                                 const minute = i.toString().padStart(2, '0');
                                 return <option key={minute} value={minute}>{minute}</option>;
                               })}
                             </select>
                           </div>
                         </div>
                       </div>
                       
                       <div className="space-y-1">
            <label className="block text-xs font-medium text-[#222c3a] dark:text-white">
                           Horário de Fim
                         </label>
                         <div className="relative">
                           <div className="flex">
                             <select
                               value={agendamentoConfig.horarios_config.fim.split(':')[0] || '18'}
                               onChange={(e) => {
                                 const minutes = agendamentoConfig.horarios_config.fim.split(':')[1] || '00';
                                 updateHorarios('fim', `${e.target.value}:${minutes}`);
                               }}
                               className="w-1/2 px-1 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-l text-[#111926] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#049769] focus:border-transparent transition-all duration-200 text-xs"
                             >
                               {Array.from({length: 24}, (_, i) => {
                                 const hour = i.toString().padStart(2, '0');
                                 return <option key={hour} value={hour}>{hour}</option>;
                               })}
                             </select>
                             <span className="flex items-center px-0.5 bg-white dark:bg-gray-700 border-t border-b border-gray-300 dark:border-gray-600 text-[#111926] dark:text-white text-xs">:</span>
                             <select
                               value={agendamentoConfig.horarios_config.fim.split(':')[1] || '00'}
                               onChange={(e) => {
                                 const hours = agendamentoConfig.horarios_config.fim.split(':')[0] || '18';
                                 updateHorarios('fim', `${hours}:${e.target.value}`);
                               }}
                               className="w-1/2 px-1 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-r text-[#111926] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#049769] focus:border-transparent transition-all duration-200 text-xs"
                             >
                               {Array.from({length: 60}, (_, i) => {
                                 const minute = i.toString().padStart(2, '0');
                                 return <option key={minute} value={minute}>{minute}</option>;
                               })}
                             </select>
                           </div>
                         </div>
                       </div>
                     </div>
                  </div>
                </div>
              </div>

              {/* Botão de Salvar */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={salvarConfiguracoes}
                  disabled={configLoading}
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-8 py-4 rounded-xl font-semibold flex items-center justify-center gap-3 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 transform hover:scale-105 relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="absolute inset-0 shimmer-effect opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <Settings className="h-5 w-5 relative z-10" />
                  <span className="relative z-10">{configLoading ? 'Salvando...' : 'Salvar Configurações'}</span>
                  <Sparkles className="h-5 w-5 relative z-10" />
                </button>
              </div>
            </div>
          </ModernCard>
        )}
      </div>
    </PageTransition>
  );
};

export default Agendamento;